import { TFunction } from 'i18next'
import { z } from 'zod'

import { ZervantLocale } from '@features/invoices/enums/zervantLocale'
import { getLegalMentionsSchema } from '@features/invoicingSettings/modules/compliance'
import {
  AutomaticPaymentRemindersType,
  getAutomaticPaymentRemindersDetailsSchema,
  getAutomaticPaymentRemindersPostAfterDueDateSchema,
  getPaymentMethodsFormSchema,
} from '@features/invoicingSettings/modules/paymentsAndReminders'
import { getCompanyContactFormSchema } from '@features/invoicingSettings/utils/getCompanyContactFormSchema'
import { ProductType } from '@features/products/enums/productType'

import { HEX_COLOR_REGEX } from '../../../../../../../constants/hexColorRegex'
import { InvoiceSettingsFormSchema } from '../types/invoiceSettingsFormSchema'

export const getInvoiceSettingsFormSchema = (
  t: TFunction,
  isFrenchComplianceMode: boolean,
): InvoiceSettingsFormSchema => {
  const legalFormSettingsSchema = z.object({
    isMemberOfAccreditedAssociation: z.boolean(),
    isVatOnDebitsEnabled: z.boolean(),
    isLegalGuaranteeOfConformity: z.boolean(),
    isSiretPendingAssignment: z.boolean().optional(),
  })
  const legalMentionsSchema = getLegalMentionsSchema(t)

  return z.object({
    automaticPaymentRemindersSettings: z.object({
      activationDate: z.date(),
      afterDueDate: getAutomaticPaymentRemindersDetailsSchema(t, AutomaticPaymentRemindersType.AfterDueDate),
      beforeDueDate: getAutomaticPaymentRemindersDetailsSchema(t, AutomaticPaymentRemindersType.BeforeDueDate),
      onDueDate: getAutomaticPaymentRemindersDetailsSchema(t, AutomaticPaymentRemindersType.OnDueDate),
      postAfterDueDate: getAutomaticPaymentRemindersPostAfterDueDateSchema(t),
    }),
    contactEmail: getCompanyContactFormSchema(t).shape.contactEmail,
    contactFirstName: z.string(),
    contactLastName: z.string(),
    contactPhone: z.string(),
    currency: z.string(),
    footerNote: z.string(),
    language: z.nativeEnum(ZervantLocale),
    legalFormSettings: isFrenchComplianceMode ? legalFormSettingsSchema : legalFormSettingsSchema.optional(),
    legalMentions: isFrenchComplianceMode ? legalMentionsSchema : legalMentionsSchema.optional(),
    logoId: z.string().nullable(),
    logoUrl: z.string().nullable().optional(),
    message: z.string(),
    paymentMethods: getPaymentMethodsFormSchema(t),
    paymentTerm: z.number(),
    productType: z.nativeEnum(ProductType),
    shouldApplyTemplateColorToColumnHeaders: z.boolean(),
    shouldApplyTemplateColorToDividers: z.boolean(),
    shouldApplyTemplateColorToHeader: z.boolean(),
    shouldApplyTemplateColorToTotal: z.boolean(),
    shouldShowCustomerId: z.boolean(),
    shouldShowTermsAndConditions: z.boolean(),
    templateColor: z
      .string()
      .regex(HEX_COLOR_REGEX, t('settings.organization.external_invoice.validation.invalid_hex_color')),
    templateFont: z.string(),
    termsAndConditions: z.string(),
    vatRateId: z.string(),
    vatRatePercent: z.number(),
  })
}
